import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { PAGE_SIZE, EVENT_GROUP_MAP, type AuditLogEntry } from './auditLogConstants'

interface UseAuditLogParams {
  page: number
  dateFrom: Date
  dateTo: Date
  eventGroups?: string[]
  actorAdminIds?: string[]
}

interface AuditLogResult {
  entries: AuditLogEntry[]
  totalCount: number
}

interface AuditLogRow {
  id: string
  event_type: string
  actor_admin_id: string | null
  target_entity_type: string | null
  target_entity_id: string | null
  target_entity_name: string | null
  changes: Record<string, unknown> | null
  reason: string | null
  note: string | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
  admin_accounts: { name: string; email: string } | null
}

function mapRow(row: AuditLogRow): AuditLogEntry {
  return {
    id: row.id,
    eventType: row.event_type,
    actorAdminId: row.actor_admin_id,
    actorName: row.admin_accounts?.name ?? null,
    actorEmail: row.admin_accounts?.email ?? null,
    targetEntityType: row.target_entity_type,
    targetEntityId: row.target_entity_id,
    targetEntityName: row.target_entity_name,
    changes: row.changes,
    reason: row.reason,
    note: row.note,
    ipAddress: row.ip_address,
    userAgent: row.user_agent,
    createdAt: row.created_at,
  }
}

async function fetchAuditLog(params: UseAuditLogParams): Promise<AuditLogResult> {
  const { page, dateFrom, dateTo, eventGroups, actorAdminIds } = params
  const from = (page - 1) * PAGE_SIZE
  const to = from + PAGE_SIZE - 1

  let query = supabase
    .from('audit_log')
    .select(
      'id, event_type, actor_admin_id, target_entity_type, target_entity_id, target_entity_name, changes, reason, note, ip_address, user_agent, created_at, admin_accounts(name, email)',
      { count: 'exact' },
    )
    .gte('created_at', dateFrom.toISOString())
    .lte('created_at', dateTo.toISOString())
    .order('created_at', { ascending: false })
    .range(from, to)

  if (eventGroups && eventGroups.length > 0) {
    const eventTypes = eventGroups.flatMap((g) => EVENT_GROUP_MAP[g] ?? [])
    if (eventTypes.length > 0) query = query.in('event_type', eventTypes)
  }

  if (actorAdminIds && actorAdminIds.length > 0) {
    query = query.in('actor_admin_id', actorAdminIds)
  }

  const { data, count, error } = await query

  if (error) throw error

  return {
    entries: (data as unknown as AuditLogRow[]).map(mapRow),
    totalCount: count ?? 0,
  }
}

export function useAuditLog(params: UseAuditLogParams) {
  return useQuery({
    queryKey: [
      'audit-log',
      params.page,
      params.dateFrom.toISOString(),
      params.dateTo.toISOString(),
      params.eventGroups?.join(','),
      params.actorAdminIds?.join(','),
    ],
    queryFn: () => fetchAuditLog(params),
  })
}